import slideDown from '../util/slide-down';
import slideUp from '../util/slide-up';
import showElement from '../util/show-element';

const selectors = {
  accordionGroup: '[data-accordion-group]',
  accordionToggle: 'data-accordion-trigger',
  accordionBody: '[data-accordion-body]',
  accordionBodyMobile: 'data-accordion-body-mobile',
  rangeSlider: 'data-range-holder',
  section: '[data-section-id]',
};

const classes = {
  open: 'accordion-is-open',
};

let sections = {};

class Accordion {
  constructor(el) {
    this.body = el;
    this.key = this.body.id;
    const btnSelector = `[${selectors.accordionToggle}='${this.key}']`;
    this.trigger = document.querySelector(btnSelector);

    this.toggleEvent = (e) => this.clickEvents(e);
    this.keyboardEvent = (e) => this.keyboardEvents(e);
    this.hideEvent = () => this.hideEvents();

    this.syncBodies = this.getSiblings();

    if (this.body.hasAttribute(selectors.accordionBodyMobile)) {
      this.mobileAccordions();
    } else {
      this.init();
    }
  }

  mobileAccordions() {
    if (window.innerWidth < window.theme.sizes.medium) {
      this.init();
      this.setDefaultState();
    } else {
      this.resetMobileAccordions();
      this.body.removeAttribute('style');
    }

    document.addEventListener('theme:resize', () => {
      if (window.innerWidth < window.theme.sizes.medium) {
        this.init();
        this.setDefaultState();
      } else {
        this.resetMobileAccordions();
        this.body.removeAttribute('style');
      }
    });
  }

  init() {
    this.trigger.setAttribute('aria-haspopup', true);
    this.trigger.setAttribute('aria-expanded', false);
    this.trigger.setAttribute('aria-controls', this.key);

    this.setDefaultState();

    this.trigger.addEventListener('click', this.toggleEvent);
    this.body.addEventListener('keyup', this.keyboardEvent);
    this.body.addEventListener('theme:accordion:close', this.hideEvent);
  }

  hideEvents() {
    this.hideAccordion();
  }

  clickEvents(e) {
    e.preventDefault();
    this.toggleState();
  }

  keyboardEvents(e) {
    if (e.code !== 'Escape') {
      return;
    }
    this.hideAccordion();
    this.trigger.focus();
  }

  resetMobileAccordions() {
    this.trigger.removeEventListener('click', this.toggleEvent);
    this.body.removeEventListener('keyup', this.keyboardEvent);
    this.body.removeEventListener('theme:accordion:close', this.hideEvent);
  }

  setDefaultState() {
    if (this.trigger.classList.contains(classes.open)) {
      showElement(this.body);
    } else {
      this.hideAccordion();
    }
  }

  getSiblings() {
    const section = this.body.closest(selectors.section);
    const groupsArray = [...section.querySelectorAll(selectors.accordionGroup)];
    const syncWrapper = groupsArray.filter((el) => el.contains(this.body)).shift();
    if (syncWrapper) {
      const allChilden = [...syncWrapper.querySelectorAll(selectors.accordionBody)];
      const onlySiblings = allChilden.filter((el) => !el.contains(this.body));
      return onlySiblings;
    } else return [];
  }

  closeSiblings() {
    this.syncBodies.forEach((accordionBody) => {
      accordionBody.dispatchEvent(new CustomEvent('theme:accordion:close', {bubbles: false}));
    });
  }

  toggleState() {
    if (this.trigger.classList.contains(classes.open)) {
      this.hideAccordion();
    } else {
      this.showAccordion();
      this.closeSiblings();

      // Collection filters
      // Accordion with range slider custom event to reload
      if (this.body.hasAttribute(selectors.rangeSlider)) {
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('theme:price-range:reset', {bubbles: false}));
        }, 400);
      }
    }
  }

  hideAccordion() {
    this.trigger.classList.remove(classes.open);
    slideUp(this.body);
  }

  showAccordion() {
    this.trigger.classList.add(classes.open);
    slideDown(this.body);

    setTimeout(() => {
      this.checkInViewportAndScrollTo();
    }, 600);
  }

  checkInViewportAndScrollTo() {
    const rect = this.trigger.getBoundingClientRect();
    const windowScrollY = (window.pageYOffset || document.documentElement.scrollTop) - (document.documentElement.clientTop || 0);
    const inViewport =
      rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth);

    if (!inViewport) {
      window.scrollTo({
        top: windowScrollY + rect.top,
        left: 0,
        behavior: 'smooth',
      });
    }
  }

  onBlockSelect(evt) {
    if (this.body.contains(evt.target)) {
      this.showAccordion();
    }
  }

  onBlockDeselect(evt) {
    if (this.body.contains(evt.target)) {
      this.hideAccordion();
    }
  }
}

const accordion = {
  onLoad() {
    sections[this.id] = [];
    const els = this.container.querySelectorAll(selectors.accordionBody);
    els.forEach((el) => {
      sections[this.id].push(new Accordion(el));
    });
  },
  onUnload: function () {
    sections[this.id].forEach((el) => {
      if (typeof el.unload === 'function') {
        el.unload();
      }
    });
  },
  onSelect: function () {
    if (this.type === 'accordion-single') {
      this.container.querySelector(`[${selectors.accordionToggle}]`).click();
    }
  },
  onDeselect: function () {
    if (this.type === 'accordion-single') {
      this.container.querySelector(`[${selectors.accordionToggle}]`).click();
    }
  },
  onBlockSelect(evt) {
    sections[this.id].forEach((el) => {
      if (typeof el.onBlockSelect === 'function') {
        el.onBlockSelect(evt);
      }
    });
  },
  onBlockDeselect(evt) {
    sections[this.id].forEach((el) => {
      if (typeof el.onBlockSelect === 'function') {
        el.onBlockDeselect(evt);
      }
    });
  },
};

export {accordion, Accordion};
